
import { Inject, Options, Prop, Vue, Watch } from "vue-property-decorator";
import SwipeStepper from "../swipe-stepper/index.vue";
import { NavStep, NavStepStatusID, Section, Step } from "../swipe-stepper/nav-step";
import { AppInsights } from "@/trust-atcs-app/appInsights";
import { TrustAPI } from "@/shared/modules/trust-api";
import { EventEmitter } from "events";
import store from "@/trust-atcs-app/store";
import { RouteN } from "@/trust-atcs-app/constants/router-name";
import LoadingIndicator from "@/shared/components/loading-indicator/index.vue";
import FormPartBOverview from "../form-part-b-overview/index.vue";
import FormPartBTrustAccountingRecords from "../form-part-b-trust-accounting-records/index.vue";
import FormPartBGeneralTrustMoney from "../form-part-b-general-trust-money/index.vue";
import FormPartBControlledMoney from "../form-part-b-controlled-money/index.vue";
import FormPartBSpecificPowerMoney from "../form-part-b-specific-power-money/index.vue";
import FormPartBInvestmentMoney from "../form-part-b-investment-money/index.vue";
import FormPartBOtherInformation from "../form-part-b-other-information/index.vue";
import FormPartBDeclaration from "../form-part-b-declaration/index.vue";
import FormPartBConfirmation from "../form-part-b-confirmation/index.vue";
import ConfirmDialogue from '@/shared/components/confirm-dialogue/index.vue';
import { ComplianceForm, PageContent } from "@/shared/modules/typescript-trustapi-client/models";
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import FormHeader from "../form-header/index.vue";
import { FormPartBData } from "@/trust-atcs-app/models/form-part-b-data";
import LoadingSpinner from "@/shared/components/loading-spinner/index.vue";
import FormHeaderTcNote from "../form-header-tc-note/index.vue";
import { IDLESessionManager } from '@/trust-atcs-app/modules/session/idle-session-manager';
import SessionExpired from "../session-expired-modal/index.vue";
import { useFormPartBDataStore } from "@/trust-atcs-app/store/useFormPartBDataStore";

@Options({
  components: {
    LoadingSpinner,
    LoadingIndicator,
    SwipeStepper,
    FormHeader,
    FormPartBOverview,
    FormPartBTrustAccountingRecords,
    FormPartBGeneralTrustMoney,
    FormPartBControlledMoney,
    FormPartBSpecificPowerMoney,
    FormPartBInvestmentMoney,
    FormPartBOtherInformation,
    FormPartBDeclaration,
    FormPartBConfirmation,
    ConfirmDialogue,
    FormHeaderTcNote,
    SessionExpired
  },
  mixins: [FormatDateTimeMixin],
  beforeRouteEnter(to: any, from: any, next: any) {
    const complianceFormB = store.getters.getSelectedComplianceFormB;
    if (!complianceFormB) {
      next({ name: RouteN.ATCS.Main.n });
    } else {
      next();
    }
  }
})
export default class FormPartB extends Vue {

  private formPartBDataStore = useFormPartBDataStore();

  private ai = new AppInsights;

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private navSteps = [
    new NavStep(Step.overview, Section.overview), // 0
    new NavStep(Step.trustAccountingRecords, Section.trustAccountingRecords),   // 1
    new NavStep(Step.generalTrustMoney, Section.generalTrustMoney), // 2
    new NavStep(Step.controlledMoney, Section.controlledMoney), // 3
    new NavStep(Step.specificPowerMoney, Section.specificPowerMoney), // 4
    new NavStep(Step.investmentMoney, Section.investmentMoney), // 5
    new NavStep(Step.otherInformation, Section.otherInformation), // 6
    new NavStep(Step.declaration, Section.declaration), // 7
    new NavStep(Step.confirmation, Section.confirmation), // 8
  ];

  private backButtonText = 'Cancel Application';
  private nextButtonText = 'Next';
  private backButtonVisible = true;

  private formName = { line1: 'Part', line2: 'B' };
  private formDesc = { line1: 'Law Practice\'s Statement of Trust Money', line2: '' };
  private isSectionLoading = false;
  private isSaving = false;
  private isClosing = false;
  private stopExtending = false;
  private showSessionExpiredModal = false;
  private workerID = -1;

  private validSection: boolean[] = [
    true, // overview
    false,  // trustAccountingRecords
    false, // generalTrustMoney
    false, // controlledMoney
    false, // specificPowerMoney
    false, // investmentMoney
    false, // otherInformation
    false, // declaration
    true, // confirmation
  ];

  private isDebugging = process.env.VUE_APP_ENV == 'development' ? true : false;
  private sectionName = '';
  private helpContent: PageContent = {};

  private formPartBData: FormPartBData = {
    trustAccountingRecords: null as any,
    generalTrustMoney: null as any,
    controlledMoney: null as any,
    specificPowerMoney: null as any,
    investmentMoney: null as any,
    otherInformation: null as any,
  };

  get $Section() {
    return Section;
  }

  private idleSessionMgr: IDLESessionManager = new IDLESessionManager(60);

  @Watch('$route')
  public OnRouteChange(val: any, oldVal: any): void {
    const _stored = 'something' //check store data here
    window.scrollTo(0, 0);
    if (oldVal.fullPath.indexOf('confirmation') > -1 || _stored == null) {
      this.$router.push({ name: RouteN.ATCS.DashboardMain.n });
    }
    this.setCurrentStep();
  }

  created(): void {
    this.formPartBDataStore.setFormPartBData(this.formPartBData);
    this.idleSessionMgr.run(this.idleSessionTimeoutCallback);
    this.workerID = setInterval(() => {
      this.extendSession();
    }, 30000);
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.eventCtrl.on('save-and-close', this.saveAndClose);
    this.eventCtrl.on('save-section', this.saveSection);
    this.eventCtrl.on('save-and-custom-action', this.saveAndCustomAction);
    this.setCurrentStep();
  }

  beforeUnmount(): void {
    clearInterval(this.workerID);
    this.eventCtrl.off('save-and-close', this.saveAndClose);
    this.eventCtrl.off('save-section', this.saveSection);
    this.eventCtrl.off('save-and-custom-action', this.saveAndCustomAction);
  }

  beforeRouteLeave(to: any, from: any, next: any): void {
    store.dispatch('setSelectedComplianceFormB', null);
    store.dispatch('setFormUpdatingkey', null);
    this.formPartBDataStore.clearFormPartBData();
  }

  private exitForm(): void {
    this.stopExtending = true;
    clearInterval(this.workerID);
    this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
    this.showSessionExpiredModal = false;
  }

  private idleSessionTimeoutCallback(): void {
    this.stopExtending = true;
  }

  private async extendSession(): Promise<void> {
    try {
      if (this.stopExtending) {
        clearInterval(this.workerID);
        this.showSessionExpiredModal = true;
        return;
      }
      if (this.isSaving) {
        return;
      }
      await this.apiService.PartBFormApi?.apiV2PartBFormExtendFormStatusPost({
        complianceFormId: this.complianceFormB.id,
        updatingKey: this.formUpdatingKey,
        manageSignatoryUpdatingKey: this.signatoriesUpdatingKey
      });

    } catch (err) {
      clearInterval(this.workerID);
      this.showSessionExpiredModal = true;
    }
  }

  private setCurrentStep(): void {
    const _section: string = this.$route.query.sec as string;
    if (_section !== Section.overview) {
      this.backButtonText = 'Back';
    } else {
      this.backButtonText = 'Cancel';
    }

    this.backButtonVisible = true;

    switch (_section) {
      case Section.overview:
        this.backButtonVisible = false;
        this.nextButtonText = 'Next';
        this.setSteps(0);
        break;
      case Section.trustAccountingRecords:
        this.nextButtonText = 'Next';
        this.setSteps(1);
        break;
      case Section.generalTrustMoney:
        this.nextButtonText = 'Next';
        this.setSteps(2);
        break;
      case Section.controlledMoney:
        this.nextButtonText = 'Next';
        this.setSteps(3);
        break;
      case Section.specificPowerMoney:
        this.nextButtonText = 'Next';
        this.setSteps(4);
        break;
      case Section.investmentMoney:
        this.nextButtonText = 'Next';
        this.setSteps(5);
        break;
      case Section.otherInformation:
        this.nextButtonText = 'Next';
        this.setSteps(6);
        break;
      case Section.declaration:
        this.nextButtonText = 'Submit';
        this.setSteps(7);
        break;
      case Section.confirmation:
        this.setSteps(8);
        this.nextButtonText = 'Close';
        this.backButtonVisible = false;
        break;
      default:
        break;
    }
  }

  private setSteps(n: number) {
    this.updateHelpContent(n);
    for (let i = 0; i < this.navSteps.length; i++) {
      this.navSteps[i].status = NavStepStatusID.NotDone
    }
    for (let i = 0; i < n; i++) {
      this.navSteps[i].status = NavStepStatusID.Done
    }
    this.navSteps[n].status = NavStepStatusID.Current
    // the confirmation page
    const _confirmation = this.navSteps.length - 1;
    if (n === _confirmation) {
      for (let i = 0; i < _confirmation; i++) {
        this.navSteps[i].status = NavStepStatusID.Unknown
      }
    } else {
      // save the current step
      // window.sessionStorage.setItem('contractData', JSON.stringify(this.contractData));
    }
  }

  private updateHelpContent(n: number) {
    switch (n) {
      case 0: // Section.overview
        this.helpContent = this.$getPopover('Help', 9);
        break;
      case 1: // Section.trustAccountingRecords
        this.helpContent = this.$getPopover('Help', 10);
        break;
      case 2: // Section.generalTrustMoney
        this.helpContent = this.$getPopover('Help', 11);
        break;
      case 3: // Section.controlledMoney
        this.helpContent = this.$getPopover('Help', 12);
        break;
      case 4: // Section.specificPowerMoney
        this.helpContent = this.$getPopover('Help', 13);
        break;
      case 5: // Section.investmentMoney 
        this.helpContent = this.$getPopover('Help', 14);
        break;
      case 6: // Section.otherInformation
        this.helpContent = this.$getPopover('Help', 15);
        break;
      case 7: // Section.declaration
        this.helpContent = this.$getPopover('Help', 16);
        break;
      case 8: // Section.confirmation
        this.helpContent = this.$getPopover('Help', 17);
        break;
      default:
        this.helpContent = {};
        break;
    }
    this.eventCtrl.emit('updateHelpContent', this.helpContent);
  }

  private goBack() {
    const _section: string = this.$route.query.sec as string;

    this.apiService.PartBFormApi?.apiV2PartBFormExtendFormStatusPost({
      complianceFormId: this.complianceFormB.id,
      updatingKey: this.formUpdatingKey,
      manageSignatoryUpdatingKey: this.signatoriesUpdatingKey
    }).then(() => {
      switch (_section) {
        case Section.overview:
          this.eventCtrl.emit('cancel-pc-renew');
          // this.$router.push({ name: RouteN.Mars.PcRenewOverview.n, query: { appId: this.$route.query.appId } });
          break;
        case Section.trustAccountingRecords:
          this.navigate(this.navSteps[0]);
          break;
        case Section.generalTrustMoney:
          this.navigate(this.navSteps[1]);
          break;
        case Section.controlledMoney:
          this.navigate(this.navSteps[2]);
          break;
        case Section.specificPowerMoney:
          this.navigate(this.navSteps[3]);
          break;
        case Section.investmentMoney:
          this.navigate(this.navSteps[4]);
          break;
        case Section.otherInformation:
          this.navigate(this.navSteps[5]);
          break;
        case Section.declaration:
          this.navigate(this.navSteps[6]);
          break;
        case Section.confirmation:
          this.eventCtrl.emit('cancel-part-b');
          break;
        default:
          break;
      }

    }).catch((err: any) => {
      this.showSessionExpiredModal = true;
    });

  }

  private navigate(s: NavStep): void {
    const temp: string = JSON.stringify(this.$route.query);
    const tempJSON = JSON.parse(temp);
    tempJSON.sec = s.routeSecName;
    this.$router.push({ query: tempJSON });
  }

  private goNext() {
    const _section = this.$route.query.sec as string;
    try {
      switch (_section) {
        case Section.overview:
          // this.saveIndividualDetails(1);
          this.saveSection(false, false).then(
            (x: any) => !x ? (this.navigate(this.navSteps[1])) : true).catch((err: any) => console.log(err));
          break;
        case Section.trustAccountingRecords:
          this.saveSection(false, false).then(
            (x: any) => !x ? (this.navigate(this.navSteps[2])) : true).catch((err: any) => console.log(err));
          break;
        case Section.generalTrustMoney:
          this.saveSection(false, false).then(
            (x: any) => !x ? (this.navigate(this.navSteps[3])) : true).catch((err: any) => console.log(err));
          break;
        case Section.controlledMoney:
          this.saveSection(false, false).then(
            (x: any) => !x ? (this.navigate(this.navSteps[4])) : true).catch((err: any) => console.log(err));
          break;
        case Section.specificPowerMoney:
          this.saveSection(false, false).then(
            (x: any) => !x ? (this.navigate(this.navSteps[5])) : true).catch((err: any) => console.log(err));
          break;
        case Section.investmentMoney:
          this.saveSection(false, false).then(
            (x: any) => !x ? (this.navigate(this.navSteps[6])) : true).catch((err: any) => console.log(err));
          break;
        case Section.otherInformation:
          this.saveSection(false, false).then(
            (x: any) => !x ? (this.navigate(this.navSteps[7])) : true).catch((err: any) => console.log(err));
          break;
        case Section.declaration:
          this.saveSection(false, false).then((x: any) => !x ? (this.navigate(this.navSteps[8])) : true);
          break;

        case Section.confirmation:
          this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
          break;
        default:
          break;
      }
    } catch {
      console.log('error');
    }

  }

  get currentSection(): string {
    return this.$route.query.sec as string;
  }

  get savingText() {
    return 'Saving...';
  }


  private async saveAndCustomAction(callback?: () => void): Promise<void> {

    try {
      await this.saveSection(true, true, true, true);
      if (callback) {
        callback();
      }
    } catch (err) {
      console.log(err);
    }
  }

  private saveAndClose(): void {
    const _confirm: any = this.$refs.confirmSaveClose;
    _confirm.show({
      title: 'Save & Close',
      message: 'By selecting the \'Save & Close\' button, any answered parts will be saved, and you will be taken back to the Homepage. Would you like to proceed?',
      okButton: 'Proceed',
      cancelButton: 'Cancel',
    }).then((res: any) => {
      if (res) {
        this.saveSection(true, true);
      }
    })
  }

  private async saveSection(close: boolean, skipValidation: boolean, skipReload?: boolean, skipGoToHome?: boolean): Promise<boolean> {
    try {
      this.isSaving = true;

      const _section = this.$route.query.sec as string;
      await this.apiService.PartBFormApi?.apiV2PartBFormExtendFormStatusPost({
        complianceFormId: this.complianceFormB.id,
        updatingKey: this.formUpdatingKey,
        manageSignatoryUpdatingKey: this.signatoriesUpdatingKey
      });
      switch (_section) {
        case Section.trustAccountingRecords:
          await this.apiService.PartBFormApi?.apiV2PartBFormSaveTrustAccountingRecordsPost({
            id: this.formPartBData?.trustAccountingRecords?.id,
            complianceFormStatus: this.formPartBData?.trustAccountingRecords?.complianceFormStatus,
            atcsMainQuestions: this.formPartBData?.trustAccountingRecords?.atcsMainQuestions,
            complianceFormId: this.complianceFormB.id,
            updatingKey: this.formUpdatingKey,
            manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-b-tar-content');
          break;
        case Section.generalTrustMoney:
          await this.apiService.PartBFormApi?.apiV2PartBFormSaveGeneralTrustMoneyPost({
            id: this.formPartBData?.generalTrustMoney?.id,
            complianceFormStatus: this.formPartBData?.generalTrustMoney?.complianceFormStatus,
            atcsMainQuestions: this.formPartBData?.generalTrustMoney?.atcsMainQuestions,
            isConfirmed: this.formPartBData?.generalTrustMoney?.isConfirmed,
            complianceFormId: this.complianceFormB.id,
            updatingKey: this.formUpdatingKey,
            manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close && !skipReload)
            this.eventCtrl.emit('reload-form-b-gtm-content');
          break;
        case Section.controlledMoney:
          await this.apiService.PartBFormApi?.apiV2PartBFormSaveControlledMoneyPost({
            id: this.formPartBData?.controlledMoney?.id,
            complianceFormStatus: this.formPartBData?.controlledMoney?.complianceFormStatus,
            atcsMainQuestions: this.formPartBData?.controlledMoney?.atcsMainQuestions,
            complianceFormId: this.complianceFormB.id,
            updatingKey: this.formUpdatingKey,
            manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-b-cm-content');
          break;
        case Section.specificPowerMoney:
          await this.apiService.PartBFormApi?.apiV2PartBFormSaveSpecificPowerMoneyPost({
            id: this.formPartBData?.specificPowerMoney?.id,
            complianceFormStatus: this.formPartBData?.specificPowerMoney?.complianceFormStatus,
            atcsMainQuestions: this.formPartBData?.specificPowerMoney?.atcsMainQuestions,
            complianceFormId: this.complianceFormB.id,
            updatingKey: this.formUpdatingKey,
            manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-b-spm-content');
          break;
        case Section.investmentMoney:
          await this.apiService.PartBFormApi?.apiV2PartBFormSaveInvestmentMoneyPost({
            id: this.formPartBData?.investmentMoney?.id,
            complianceFormStatus: this.formPartBData?.investmentMoney?.complianceFormStatus,
            atcsMainQuestions: this.formPartBData?.investmentMoney?.atcsMainQuestions,
            complianceFormId: this.complianceFormB.id,
            updatingKey: this.formUpdatingKey,
            manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-b-im-content');
          break;
        case Section.otherInformation:
          await this.apiService.PartBFormApi?.apiV2PartBFormSaveOtherInformationPost({
            id: this.formPartBData?.otherInformation?.id,
            complianceFormStatus: this.formPartBData?.otherInformation?.complianceFormStatus,
            atcsMainQuestions: this.formPartBData?.otherInformation?.atcsMainQuestions,
            complianceFormId: this.complianceFormB.id,
            updatingKey: this.formUpdatingKey,
            manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
            skipValidation: skipValidation,
          });

          if (skipValidation && !close)
            this.eventCtrl.emit('reload-form-b-oi-content');
          break;
        case Section.declaration:
          if (skipValidation && !close) {
            await this.apiService.PartBFormApi?.apiV2PartBFormSaveDeclarationPost({
              complianceFormId: this.complianceFormB.id,
              examinationCost: this.formPartBData.declarationDetails?.examinationCost,
              recordsProvidedOn: this.formPartBData?.declarationDetails.recordsProvidedOn,
              manageSignatoryUpdatingKey: this.signatoriesUpdatingKey,
              updatingKey: this.formUpdatingKey,
            });
          } else if (!close) {
            await this.apiService.PartBFormApi?.apiV2PartBFormSubmitFormPost({
              complianceFormId: this.complianceFormB.id,
              examinationCost: this.formPartBData?.declarationDetails?.examinationCost,
              recordsProvidedOn: this.formPartBData?.declarationDetails?.recordsProvidedOn,
              updatingKey: this.formUpdatingKey,
              manageSignatoryUpdatingKey: this.signatoriesUpdatingKey
            });
          }
          break;
        default:
          break;
      }

      if (close) {
        // this.isClosing = true;
        await this.apiService.PartBFormApi?.apiV2PartBFormClosePost({
          complianceFormId: this.complianceFormB.id,
          updatingKey: this.formUpdatingKey ?? null,
          manageSignatoryUpdatingKey: this.signatoriesUpdatingKey ?? null
        });
        // this.isClosing = false;
        if (!skipGoToHome)
          this.$router.push({ name: RouteN.ATCS.DashboardHome.n });
      }
      return Promise.resolve(false);
    } catch (err) {
      // this.isClosing = false;
      // this.showSessionExpiredModal = true;
      this.eventCtrl.emit('show-snackbar', 'error', 'Error saving section');
      return Promise.reject(true);
    } finally {
      this.isSaving = false;
    }
  }

  private validSubQuestion(i: number): void {
    console.log('validSubQuestion: ', i);
  }

  private validationForm(i: any, isValid: any) {
    this.validSection[i] = isValid;
    this.canGoNext();
  }

  private canGoNext(): boolean {
    // check if the current section is valid
    const _section = this.$route.query.sec as string;

    switch (_section) {
      case Section.overview:
        return this.validSection[0];
      case Section.trustAccountingRecords:
        return this.validSection[1];
      case Section.generalTrustMoney:
        return this.validSection[2];
      case Section.controlledMoney:
        return this.validSection[3];
      case Section.specificPowerMoney:
        return this.validSection[4];
      case Section.investmentMoney:
        return this.validSection[5];
      case Section.otherInformation:
        return this.validSection[6];
      case Section.declaration:
        return this.validSection[7];
      case Section.confirmation:
        return true;
      default:
        return false;
    }
  }

  get complianceFormB(): ComplianceForm {
    return store.getters.getSelectedComplianceFormB;
  }

  get formUpdatingKey(): string {
    return store.getters.getFormUpdatingKey;
  }

  get signatoriesPracticeCoreId(): number | null {
    return store.getters.getSelectedPracticeCoreId;
  }

  get signatoriesUpdatingKey(): string {
    return store.getters.getSignatoriesUpdatingKey;
  }

  private setFormDescription(message: any) {
    if (message) {
      this.formDesc = { line1: 'Statement of Trust Money (Part B) as at ' + message, line2: '' };
    }
  }

}

